import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, usePortalAuth, extractErr } from "@/portal/PortalAuth";
import { PortalHeader, ChangePasswordGate, ghost, card } from "@/portal/PortalShell";
import { UNITS } from "@/data/units";

export default function HomeownerDashboard() {
  return <ChangePasswordGate><Inner /></ChangePasswordGate>;
}

function Inner() {
  const { user } = usePortalAuth();
  const [data, setData] = useState({ assigned_unit_slugs: [], occupancy: [], maintenance: [] });
  useEffect(() => {
    api.get("/homeowner/me").then((r) => setData(r.data)).catch((e) => toast.error(extractErr(e)));
  }, []);
  const units = UNITS.filter((u) => data.assigned_unit_slugs?.includes(u.slug));
  return (
    <section data-testid="homeowner-dashboard" className="px-6 md:px-12 pt-28 md:pt-32 pb-24">
      <div className="max-w-5xl mx-auto">
        <PortalHeader subtitle="Homeowner Portal" title={`Welcome, ${user.name}`} />

        <div className={card} data-testid="ho-properties">
          <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">Your properties</p>
          {units.length === 0 && <p className="mt-3 text-[#78716C]">No properties assigned yet. Please contact Masaya Hospitality.</p>}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {units.map((u) => {
              const activeStays = (data.occupancy || []).filter((s) => s.unit_slug === u.slug);
              return (
                <div key={u.slug} className="border border-[#E7E5E4] rounded-xl overflow-hidden" data-testid={`ho-unit-${u.slug}`}>
                  <img src={u.cover} alt={u.name} className="w-full aspect-[4/3] object-cover" />
                  <div className="p-5">
                    <p className="text-xs uppercase tracking-wider text-[#8A9A86]">{u.area}</p>
                    <p className="mt-1 font-medium text-[#1C1917]">{u.name}</p>
                    <p className="mt-1 text-sm text-[#44403C]">{u.bathroom} · {u.bed}</p>
                    <p className="mt-2 text-sm"><span className="text-[#78716C]">Current occupancy:</span> <span className="text-[#1C1917] font-medium">{activeStays.length} active {activeStays.length === 1 ? "stay" : "stays"}</span></p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={`${card} mt-6`} data-testid="ho-maintenance">
          <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">Recent maintenance activity</p>
          {(data.maintenance || []).length === 0 && <p className="mt-3 text-[#78716C]">No maintenance activity yet.</p>}
          <div className="mt-4 space-y-3">
            {(data.maintenance || []).map((m) => (
              <div key={m.id} className="border border-[#E7E5E4] rounded-lg p-4 text-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="font-medium text-[#1C1917]">{m.category} · <span className="text-[#78716C] font-light">{m.unit_slug}</span></p>
                  <span className="text-xs px-2 py-1 rounded-full bg-[#F5F5F4] text-[#44403C]">{m.status}</span>
                </div>
                <p className="mt-1 text-[#44403C]">{m.message}</p>
                <p className="mt-1 text-xs text-[#78716C]">Submitted {m.created_at?.slice(0, 10)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className={`${card} mt-6`} data-testid="ho-financials">
          <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">Owner statements &amp; financial reports</p>
          <p className="mt-2 text-[#44403C] font-light">Detailed statements and payout reports are coming soon.</p>
        </div>

        <div className="mt-6 flex gap-3">
          <Link to="/contact" className={ghost}>Message Masaya Hospitality</Link>
        </div>
      </div>
    </section>
  );
}
